import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { Customer, EntityMeta } from '@qtek/shared/models';
import { NewRelationsActions } from './new-relations.actions';

export interface RelationsState {
  people: Record<string, Customer[]>;
  peopleMeta: Record<string, EntityMeta>;
  institutions: Customer[];
  availableInstitutions: Customer[];
  peopleLoading: Record<string, boolean>;
  institutionLoading: boolean;
  availableInstitutionsLoading: boolean;
  institutionsMeta: EntityMeta | null;
  availableInstitutionsMeta: EntityMeta | null;
  error: Error | null;
  selectedCustomer: Customer;
  selectedCompany: Customer | any;
}

export const initialState: RelationsState = {
  people: {},
  peopleMeta: {},
  peopleLoading: {},
  error: null,
  institutions: [],
  institutionLoading: false,
  availableInstitutions: [],
  availableInstitutionsLoading: false,
  institutionsMeta: null,
  availableInstitutionsMeta: null,
  selectedCustomer: null,
  selectedCompany: null,
};

export const relationsKey = 'NewRelationFeature';

export const relationsReducer = createReducer(
  initialState,
  on(NewRelationsActions.subscribePeople, (state, { mysid }) => ({
    ...state,
    peopleLoading: {
      ...state.peopleLoading,
      [mysid]: true,
    },
  })),
  on(
    NewRelationsActions.getPeopleSuccess,
    (state, { customers, meta, mysid }) => {
      return {
        ...state,
        people: {
          ...state.people,
          [mysid]: customers,
        },
        peopleLoading: {
          ...state.peopleLoading,
          [mysid]: false,
        },
        peopleMeta: {
          ...state.peopleMeta,
          [mysid]: meta,
        },
      };
    }
  ),
  on(NewRelationsActions.unsubscribePeople, (state, { mysid }) => {
    if (!mysid) return state;

    const { [mysid]: removedPeople, ...people } = state.people;
    const { [mysid]: removedPeopleMeta, ...peopleMeta } = state.peopleMeta;
    const { [mysid]: removedPeopleLoading, ...peopleLoading } =
      state.peopleLoading;

    return {
      ...state,
      people,
      peopleMeta,
      peopleLoading,
    };
  }),
  on(NewRelationsActions.getPeopleFailure, (state, { error, mysid }) => {
    const { [mysid]: removedPeopleMeta, ...peopleMeta } = state.peopleMeta;

    return {
      ...state,
      people: {
        ...state.people,
        [mysid]: [] as Customer[],
      },
      peopleLoading: {
        ...state.peopleLoading,
        [mysid]: false,
      },
      peopleMeta,
    };
  }),
  on(NewRelationsActions.subscribeInstitutions, state => ({
    ...state,
    institutionLoading: true,
  })),
  on(
    NewRelationsActions.getInstitutionsSuccess,
    (state, { customers, meta }) => ({
      ...state,
      institutions: customers,
      institutionLoading: false,
      institutionsMeta: meta,
    })
  ),
  on(NewRelationsActions.getInstitutionsFailure, state => ({
    ...state,
    institutionLoading: false,
  })),
  on(NewRelationsActions.updatePeopleSuccess, (state, { customer, mysid }) => {
    if (!mysid) return state;

    const existingPeople = state.people[mysid] || [];

    return {
      ...state,
      people: {
        ...state.people,
        [mysid]: existingPeople.map(currentCustomer => ({
          ...(customer.relId === currentCustomer.relId
            ? customer
            : currentCustomer),
        })),
      },
    };
  }),
  on(NewRelationsActions.updateInstitutionsSuccess, (state, { customer }) => ({
    ...state,
    institutions: state.institutions.map(currentCustomer => ({
      ...(customer.acn._id === currentCustomer.acn._id
        ? customer
        : currentCustomer),
    })),
  })),
  on(NewRelationsActions.createPeopleSuccess, (state, { customer, mysid }) => {
    if (!mysid) return state;

    const existingPeople = state.people[mysid] || [];

    return {
      ...state,
      people: {
        ...state.people,
        [mysid]: [...existingPeople, customer],
      },
    };
  }),
  on(NewRelationsActions.createInstitutionsSuccess, (state, { customer }) => ({
    ...state,
    institutions: state.institutions.concat(customer),
  })),
  on(NewRelationsActions.deletePeopleSuccess, (state, { customer, mysid }) => {
    if (!mysid) return state;

    const existingPeople = state.people[mysid] || [];

    return {
      ...state,
      people: {
        ...state.people,
        [mysid]: existingPeople.filter(
          currentCustomer => currentCustomer.relId !== customer.relId
        ),
      },
    };
  }),
  on(NewRelationsActions.deleteInstitutionSuccess, (state, { customer }) => ({
    ...state,
    institutions: state.institutions.filter(
      currentCustomer => currentCustomer.relId !== customer.relId
    ),
  })),
  on(NewRelationsActions.getCustomerByIdSuccess, (state, { customer }) => ({
    ...state,
    selectedCustomer: customer,
  })),
  on(
    NewRelationsActions.getCustomerByIdFailure,
    (state, { error }): RelationsState => ({
      ...state,
      selectedCustomer: null,
      error,
    })
  ),
  on(
    NewRelationsActions.getSingleInstitutionSuccess,
    (state, { institution }) => ({
      ...state,
      selectedCompany: institution,
    })
  ),
  on(
    NewRelationsActions.getSingleInstitutionFailure,
    (state, { error }): RelationsState => ({
      ...state,
      selectedCompany: null,
      error,
    })
  ),
  on(NewRelationsActions.subscribeAvailableInstitutions, state => ({
    ...state,
    availableInstitutionsLoading: true,
  })),
  on(
    NewRelationsActions.getAvailableInstitutionsSuccess,
    (state, { customers, meta }) => ({
      ...state,
      availableInstitutions: customers,
      availableInstitutionsLoading: false,
      availableInstitutionsMeta: meta,
    })
  ),
  on(
    NewRelationsActions.getAvailableInstitutionsFailure,
    (state, { error }) => ({
      ...state,
      availableInstitutionsLoading: false,
      error,
    })
  ),
  on(NewRelationsActions.clearAvailableInstitutions, state => ({
    ...state,
    availableInstitutions: [] as Customer[],
    availableInstitutionsMeta: null as EntityMeta | null,
    availableInstitutionsLoading: false,
  }))
);

export const selectPeople = (state: RelationsState) => state.people;
export const selectPeopleLoading = (state: RelationsState) =>
  state.peopleLoading;
export const selectInstitutions = (state: RelationsState) => state.institutions;
export const selectInstitutionLoading = (state: RelationsState) =>
  state.institutionLoading;
export const selectInstitutionsMeta = (state: RelationsState) =>
  state.institutionsMeta;
export const selectAvailableInstitutions = (state: RelationsState) =>
  state.availableInstitutions;
export const selectAvailableInstitutionsLoading = (state: RelationsState) =>
  state.availableInstitutionsLoading;
export const selectAvailableInstitutionsMeta = (state: RelationsState) =>
  state.availableInstitutionsMeta;
// export const getRelationsMeta = (state: RelationsState) => state.meta;

export const NewRelationsFeature = createFeature({
  name: relationsKey,
  reducer: relationsReducer,
  extraSelectors: ({
    selectPeople,
    selectPeopleMeta,
    selectPeopleLoading,
  }) => ({
    selectContactsByMysid: (mysid: string) =>
      createSelector(selectPeople, people => people[mysid] || []),
    selectContactsMetaByMysid: (mysid: string) =>
      createSelector(selectPeopleMeta, peopleMeta => peopleMeta[mysid] || null),
    selectContactsLoadingByMysid: (mysid: string) =>
      createSelector(
        selectPeopleLoading,
        peopleLoading => peopleLoading[mysid] || false
      ),
  }),
});
